import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import config from 'config';
import { getTimeZoneDate } from 'web-common/scripts/utils/DateHelper';
import {
    formatDate,
    formatTime,
    getDiffInMinutes,
    humanizeMinutesDuration,
    getUtcStartOfDay
} from 'web-common/scripts/utils/DateHelper';

import {
    isSessionLive
} from './sessionBroadcastUtils';
import FeatureToggleUtils from 'FeatureToggleUtils';

const RenderSessionLength = ({ startDate, endDate, startMinute, endMinute }) => {
    let minutes = 0;
    if (startDate === endDate) {
        minutes = endMinute - startMinute;
    }

    if (endDate > startDate) {
        minutes = getDiffInMinutes(startDate, endDate, startMinute, endMinute);
    }
    return humanizeMinutesDuration(minutes);
};

RenderSessionLength.propTypes = {
    startDate: PropTypes.number,
    endDate: PropTypes.number,
    startMinute: PropTypes.number,
    endMinute: PropTypes.number
};

const RenderStartTime = ({
    shouldRenderStartDate,
    session,
    settings
}) => {
    const { startDate, startMinute } = session || {};
    const { dateFormat, datesLabelType, datesLabelMapping } = settings || {};
    const timeFormat = 'h:mm A';
    const isDatesLabelsEnabled = datesLabelType === 'LABELS';
    const dateOrLabel = isDatesLabelsEnabled ? datesLabelMapping[`day-${startDate}`] : formatDate(startDate, dateFormat);
    return shouldRenderStartDate && dateOrLabel ?
        <span className="session-time start-time text-bold bz-capitalize">
            {dateOrLabel}, {formatTime(startMinute, timeFormat)}
        </span>
        : <span className="session-time start-time text-bold bz-capitalize">{formatTime(startMinute, timeFormat)}</span>;
};

RenderStartTime.propTypes = {
    shouldRenderStartDate: PropTypes.bool,
    session: PropTypes.object.isRequired,
    settings: PropTypes.object.isRequired
};

const RenderEndTime = ({
    session: { endDate, endMinute },
    settings: { dateFormat, datesLabelType, datesLabelMapping },
    shouldRenderEndDate
}) => {
    const timeFormat = 'h:mm A';
    const isDatesLabelsEnabled = datesLabelType === 'LABELS';
    const utcStartOfDay = getUtcStartOfDay(endDate);
    const dateOrLabel = isDatesLabelsEnabled ? datesLabelMapping[`day-${utcStartOfDay}`] : formatDate(endDate, dateFormat);
    if (shouldRenderEndDate && dateOrLabel) {
        return <span className="session-time end-time text-bold bz-capitalize">&nbsp;- {dateOrLabel}, {formatTime(endMinute, timeFormat)}</span>;
    } else if (endDate !== undefined) {
        return <span className="session-time end-time text-bold bz-capitalize">&nbsp;- {formatTime(endMinute, timeFormat)}</span>;
    }
    return null;
};

RenderEndTime.propTypes = {
    session: PropTypes.object.isRequired,
    settings: PropTypes.object.isRequired
};

export const transformToLocalDateAndMinutes = (eventTimezone, userTimezone, dateTimestamp, minutes) => {
    const momentEventTimezoneTime = getTimeZoneDate(dateTimestamp, minutes, eventTimezone);
    const momentLocalDate = momentEventTimezoneTime.tz(userTimezone);
    const momentLocalMidnight = momentLocalDate.clone().startOf('day');
    const localDate = moment.utc(momentLocalMidnight.format('YYYY-MM-DD HH:mm'));
    const localMinute = momentLocalDate.diff(momentLocalMidnight, 'm');
    return { localDate, localMinute };
};

const LocalTime = ({
    session: {
        startDate,
        startMinute,
        endDate,
        endMinute
    },
    settings: { dateFormat },
    event,
    numberOfLines = 1,
    userTimezone
}) => {
    const eventTimezone = event['time-zone-id'];

    const momentEventTimzoneSessionStart = getTimeZoneDate(startDate, startMinute, eventTimezone);

    const momentLocalTimezonSessionStart = momentEventTimzoneSessionStart.clone().tz(userTimezone);

    const isStartingInSameDay = momentEventTimzoneSessionStart.format('DD') === momentLocalTimezonSessionStart.format('DD');


    const {
        localDate: startLocalDate, localMinute: startLocalMinute
    } = transformToLocalDateAndMinutes(eventTimezone, userTimezone, startDate, startMinute);


    const {
        localDate: endLocalDate, localMinute: endLocalMinute
    } = transformToLocalDateAndMinutes(eventTimezone, userTimezone, endDate, endMinute);

    const isOneDaySession = getUtcStartOfDay(startLocalDate) === getUtcStartOfDay(endLocalDate);

    const shouldRenderStartDate = !isStartingInSameDay || !isOneDaySession;

    const startTimeProps = {
        shouldRenderStartDate,
        session: { startDate: startLocalDate, startMinute: startLocalMinute },
        settings: { dateFormat },
    };

    const endTimeProps = {
        shouldRenderEndDate: !isOneDaySession,
        session: { startDate: startLocalDate, endDate: endLocalDate, endMinute: endLocalMinute },
        settings: { dateFormat },
    };

    return (
        <Fragment>
            {numberOfLines === 1 && <span className="session-time">&nbsp;/&nbsp;</span>}
            <RenderStartTime {...startTimeProps} />
            <RenderEndTime {...endTimeProps} />
            <span className="session-info-text-light">&nbsp;Your local time</span>
        </Fragment>
    );
};

export {
    RenderSessionLength,
    RenderStartTime,
    RenderEndTime
};

class SessionInfo extends Component {
    static propTypes = {
        session: PropTypes.object.isRequired,
        settings: PropTypes.object.isRequired,
        shouldRenderStartDate: PropTypes.bool,
        me: PropTypes.object,
        eventUrls: PropTypes.object,
        showEmailLogin: PropTypes.func,
        event: PropTypes.object.isRequired,
        numberOfLines: PropTypes.number
    };

    static defaultProps = {
        shouldRenderStartDate: false
    };

    renderLiveNowLabel = () => {
        const {session, event} = this.props;
        let isSessionLiveNow = false;
        const isFeatureSessionVirtualElementEnabled = FeatureToggleUtils.isFeatureEnabled('feature-virtual-enable-virtual-suite');

        if (
            isFeatureSessionVirtualElementEnabled &&
            session?.enableVirtualSession &&
            session?.startDate !== 'unscheduled'
        ) {
            const timeZone = event && event['time-zone-id'];
            isSessionLiveNow = isSessionLive(session, timeZone);
        }

        return (
            isSessionLiveNow ? <span className='live-label'>
                <span className='circle-live-now'></span> LIVE
            </span> : null
        );
    }

    renderTimeInfo = () => {
        const { session: { startDate, endDate, startMinute, endMinute }, event, numberOfLines = 1 } = this.props;
        const eventTimezone = event['time-zone-id'];
        const momentEventDate = getTimeZoneDate(startDate, startMinute, eventTimezone);
        const timezoneAbbr = moment.tz.zone(eventTimezone).abbr(momentEventDate.valueOf());

        const offsetInHours = momentEventDate.utcOffset() / 60;

        const userTimezone = moment.tz.guess();
        const momentUserEventDate = getTimeZoneDate(startDate, startMinute, userTimezone);
        const userOffsetInHours = momentUserEventDate.utcOffset() / 60;

        const isAmericasOffset = offsetInHours >= -10 && offsetInHours <= -3;

        const shouldShowTimezoneAbbr = isAmericasOffset && isNaN(Number(timezoneAbbr));

        const gmt = offsetInHours >= 0 ? `GMT +${offsetInHours}` : `GMT ${offsetInHours}`;
        const abbreviationOrGMT = shouldShowTimezoneAbbr ? timezoneAbbr : gmt;

        const isValidTime = typeof startMinute === 'number' && typeof endMinute === 'number';
        const isOneDaySession = getUtcStartOfDay(startDate) === getUtcStartOfDay(endDate);
        const shouldRenderStartDate = this.props.shouldRenderStartDate || !isOneDaySession;

        const shouldShowLocalTime = offsetInHours !== userOffsetInHours;

        return (
            <time className="margin-right-small text-uppercase">
                {isValidTime && <RenderStartTime {...{ ...this.props, shouldRenderStartDate }} />}
                {isValidTime && <RenderEndTime {...{...this.props, shouldRenderEndDate: !isOneDaySession}} />}
                <span className="session-info-text-light">&nbsp;{abbreviationOrGMT}</span>
                {
                    shouldShowLocalTime &&
                    numberOfLines && numberOfLines !== 1 &&
                    <br />
                }
                {shouldShowLocalTime && <LocalTime {...{ ...this.props, userTimezone }} />}
                {numberOfLines === 3 && <br />}
                {
                    isValidTime
                        ? <span className="session-duration session-info-text-light bz-capitalize">
                            {numberOfLines !== 3 && <span>&nbsp;&nbsp;&nbsp;</span>}
                            (<RenderSessionLength {...this.props.session}/>)
                        </span>
                        : null
                }
            </time>
        );
    };


    renderNotScheduled = () => {
        const { settings } = this.props;
        const { datesLabelType, datesLabelMapping } = settings || {};
        const isDatesLabelsEnabled = datesLabelType === 'LABELS';
        const dateOrLabel = isDatesLabelsEnabled ? datesLabelMapping['not-scheduled'] : 'NOT SCHEDULED YET';
        return (
            <span className='margin-right-small'>{dateOrLabel}</span>
        );
    }

    render() {
        const { session } = this.props;
        const { startDate, id } = session || {};
        return (
            <div className="text-uppercase">
                {this.renderLiveNowLabel()}
                {id && (
                    startDate && startDate !== 'unscheduled'
                        ? this.renderTimeInfo()
                        : this.renderNotScheduled()
                )}
            </div>
        );
    }
}

export default SessionInfo;
